import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../api";
import { STANDARDPROFILE } from "@bidi-takeoff/lv-shared/profiles";
import type {
  AmpelStatus,
  LvPosition,
  LvProject,
  LvProjectSettings,
} from "@bidi-takeoff/lv-shared";
import { KatalogVorschlaegeSidebar } from "../components/KatalogVorschlaegeSidebar";

type StatusFilter = "alle" | "gruen" | "gelb_gruen" | "gelb" | "rot";

const STATUS_COLORS: Record<AmpelStatus, { bg: string; text: string; bar: string }> = {
  gruen: { bg: "bg-emerald-100", text: "text-emerald-900", bar: "bg-emerald-500" },
  gelb_gruen: { bg: "bg-lime-100", text: "text-lime-900", bar: "bg-lime-500" },
  gelb: { bg: "bg-amber-100", text: "text-amber-900", bar: "bg-amber-500" },
  rot: { bg: "bg-red-100", text: "text-red-900", bar: "bg-red-500" },
};

export default function LvPage() {
  const params = useParams<{ lvRunId: string }>();
  const lvRunId = params.lvRunId!;

  const [project, setProject] = useState<LvProject | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filterHg, setFilterHg] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<StatusFilter>("alle");
  const [editingOz, setEditingOz] = useState<string | null>(null);
  const [editingDraft, setEditingDraft] = useState<Partial<LvPosition>>({});
  const [expandedOz, setExpandedOz] = useState<string | null>(null);
  // Sprint X.2 — Tree-Aufklapp-State (default: alles aufgeklappt).
  const [collapsedHgs, setCollapsedHgs] = useState<Set<string>>(new Set());
  const [collapsedTitels, setCollapsedTitels] = useState<Set<string>>(
    new Set()
  );
  const [expandedAufmass, setExpandedAufmass] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .lvRunGet(lvRunId)
      .then(({ project }) => {
        if (!cancelled) setProject(project);
      })
      .catch((err) => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [lvRunId]);

  const filtered = useMemo(() => {
    if (!project) return [];
    return project.positions
      .filter((p) => !filterHg || p.hauptgruppe.code === filterHg)
      .filter((p) => filterStatus === "alle" || p.status === filterStatus)
      .sort((a, b) => a.ozAuto.localeCompare(b.ozAuto));
  }, [project, filterHg, filterStatus]);

  /**
   * Sprint X.2 (2026-05-05) — Tree-Struktur für die LV-Tabelle.
   * Reihen sind interleaved: HG-Header → Titel-Header → Position-Zeilen.
   * State `collapsedHgs` / `collapsedTitels` steuert das Aufklappen; alles
   * default aufgeklappt, User kann zuklappen.
   */
  type TreeRow =
    | {
        kind: "hg";
        code: string;
        name: string;
        positionCount: number;
        sumByEinheit: Record<string, number>;
      }
    | {
        kind: "titel";
        hgCode: string;
        titelCode: string;
        titelName: string;
        positionCount: number;
        sumByEinheit: Record<string, number>;
      }
    | { kind: "pos"; pos: LvPosition };

  const tree = useMemo((): TreeRow[] => {
    const rows: TreeRow[] = [];
    if (!filtered.length) return rows;
    // Pro HG → pro Titel → Positionen
    const byHg = new Map<
      string,
      {
        code: string;
        name: string;
        titels: Map<
          string,
          { titelCode: string; titelName: string; positions: LvPosition[] }
        >;
      }
    >();
    for (const p of filtered) {
      const hgKey = p.hauptgruppe.code;
      let hg = byHg.get(hgKey);
      if (!hg) {
        hg = {
          code: p.hauptgruppe.code,
          name: p.hauptgruppe.name,
          titels: new Map(),
        };
        byHg.set(hgKey, hg);
      }
      const titelKey = p.titel.code;
      let titel = hg.titels.get(titelKey);
      if (!titel) {
        titel = {
          titelCode: p.titel.code,
          titelName: p.titel.name,
          positions: [],
        };
        hg.titels.set(titelKey, titel);
      }
      titel.positions.push(p);
    }
    for (const hg of byHg.values()) {
      let hgPositionCount = 0;
      const hgSum: Record<string, number> = {};
      for (const t of hg.titels.values()) {
        hgPositionCount += t.positions.length;
        for (const p of t.positions) {
          hgSum[p.einheit] = (hgSum[p.einheit] ?? 0) + p.menge;
        }
      }
      rows.push({
        kind: "hg",
        code: hg.code,
        name: hg.name,
        positionCount: hgPositionCount,
        sumByEinheit: hgSum,
      });
      if (collapsedHgs.has(hg.code)) continue;
      for (const t of hg.titels.values()) {
        const titelSum: Record<string, number> = {};
        for (const p of t.positions) {
          titelSum[p.einheit] = (titelSum[p.einheit] ?? 0) + p.menge;
        }
        rows.push({
          kind: "titel",
          hgCode: hg.code,
          titelCode: t.titelCode,
          titelName: t.titelName,
          positionCount: t.positions.length,
          sumByEinheit: titelSum,
        });
        const titelKey = `${hg.code}.${t.titelCode}`;
        if (collapsedTitels.has(titelKey)) continue;
        for (const p of t.positions) {
          rows.push({ kind: "pos", pos: p });
        }
      }
    }
    return rows;
  }, [filtered, collapsedHgs, collapsedTitels]);

  const formatSumByEinheit = (sum: Record<string, number>): string => {
    const parts = Object.entries(sum)
      .filter(([, v]) => v > 0)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([einheit, menge]) => `${menge.toFixed(2)} ${einheit}`);
    return parts.join(" / ");
  };

  const toggleHg = (code: string) =>
    setCollapsedHgs((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });

  const toggleTitel = (hgCode: string, titelCode: string) =>
    setCollapsedTitels((prev) => {
      const next = new Set(prev);
      const key = `${hgCode}.${titelCode}`;
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const statusCounts = useMemo(() => {
    const c: Record<AmpelStatus, number> = {
      gruen: 0,
      gelb_gruen: 0,
      gelb: 0,
      rot: 0,
    };
    if (!project) return c;
    for (const p of project.positions) {
      if (p.status) c[p.status] += 1;
    }
    return c;
  }, [project]);

  const klaerCount = useMemo(
    () =>
      project?.positions.filter(
        (p) => p.status === "rot" || p.status === "gelb"
      ).length ?? 0,
    [project]
  );

  const patchSettings = async (patch: Partial<LvProjectSettings>) => {
    try {
      const { project: next } = await api.lvRunPatchSettings(lvRunId, patch);
      setProject(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const startEdit = (p: LvPosition) => {
    setEditingOz(p.ozAuto);
    setEditingDraft({
      bezeichnung: p.bezeichnung,
      menge: p.menge,
      einheit: p.einheit,
      raum: p.raum,
      etage: p.etage,
      wohneinheit: p.wohneinheit,
    });
  };
  const cancelEdit = () => {
    setEditingOz(null);
    setEditingDraft({});
  };
  /**
   * Sprint Z (2026-05-05) — Patch eines einzelnen Fachplaner-Nachweis-Status
   * an einer Position. Mergt mit existierenden nachweise und schickt komplettes
   * `nachweise`-Objekt an den Backend-PATCH-Endpoint.
   */
  const patchNachweisStatus = async (
    p: LvPosition,
    fachplaner: string,
    nextStatus:
      | "fehlt"
      | "angefordert"
      | "vorliegt"
      | "geprueft"
      | "nicht-erforderlich"
  ) => {
    const merged = { ...(p.nachweise ?? {}) } as NonNullable<
      LvPosition["nachweise"]
    >;
    const existing = merged[fachplaner as keyof typeof merged];
    merged[fachplaner as keyof typeof merged] = {
      status: nextStatus,
      quelle: existing?.quelle,
      vermerk: existing?.vermerk,
      pruefungAm: new Date().toISOString(),
    };
    try {
      const { position } = await api.lvRunPatchPosition(lvRunId, p.ozAuto, {
        nachweise: merged,
      });
      setProject((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          positions: prev.positions.map((q) =>
            q.ozAuto === p.ozAuto ? position : q
          ),
        };
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  /**
   * LV-Katalog Phase 2 (2026-05-07) — übernimmt einen Katalog-Treffer in
   * eine LvPosition: Bezeichnung + Langtext + ozKatalog + katalogVersion.
   */
  const applyKatalogTreffer = async (
    p: LvPosition,
    treffer: {
      ozKatalog: string;
      kurztext: string;
      langtext: string;
      einheit: string;
      katalogVersion: string;
    },
  ) => {
    try {
      const { position } = await api.lvRunPatchPosition(lvRunId, p.ozAuto, {
        bezeichnung: treffer.kurztext,
        langtext: treffer.langtext,
        ozKatalog: treffer.ozKatalog,
        katalogVersion: treffer.katalogVersion,
      });
      setProject((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          positions: prev.positions.map((q) =>
            q.ozAuto === p.ozAuto ? position : q,
          ),
        };
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const savePositionEdit = async () => {
    if (!editingOz) return;
    try {
      const { position } = await api.lvRunPatchPosition(
        lvRunId,
        editingOz,
        editingDraft
      );
      setProject((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          positions: prev.positions.map((p) =>
            p.ozAuto === editingOz ? position : p
          ),
        };
      });
      cancelEdit();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  if (error) {
    return (
      <div className="max-w-5xl mx-auto p-6">
        <Link to="/multiagent" className="text-sm text-brand-600 hover:underline">
          ← Zur Multi-Agent-Liste
        </Link>
        <div className="mt-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded">
          {error}
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="max-w-5xl mx-auto p-6 text-slate-500">
        Lädt LV-Daten…
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-3.5rem)]">
      {/* Sidebar */}
      <aside className="w-80 border-r overflow-y-auto bg-slate-50 flex-shrink-0">
        <div className="p-4 border-b">
          <Link
            to={`/multiagent/${project.sourceRunId}`}
            className="text-xs text-brand-600 hover:underline"
          >
            ← Zurück zum Multi-Agent ({project.sourceRunId})
          </Link>
          <h2 className="mt-2 text-base font-semibold">{project.lvRunId}</h2>
          <p className="text-xs text-slate-500 break-all">
            erstellt {new Date(project.createdAt).toLocaleString("de-DE")}
          </p>
        </div>

        {/* Settings */}
        <div className="p-4 border-b">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">
            Projekt-Settings
          </h3>
          <div className="space-y-2 text-xs">
            <div>
              <label className="block text-slate-500 mb-1">Projektart</label>
              <select
                value={project.settings.projektart}
                onChange={(e) =>
                  patchSettings({
                    projektart: e.target.value as "Neubau" | "Sanierung",
                  })
                }
                className="w-full px-2 py-1 border rounded"
              >
                <option value="Neubau">Neubau</option>
                <option value="Sanierung">Sanierung</option>
              </select>
            </div>
            <div>
              <label className="block text-slate-500 mb-1">Qualität</label>
              <select
                value={project.settings.qualitaetsstandard}
                onChange={(e) =>
                  patchSettings({
                    qualitaetsstandard: e.target
                      .value as LvProjectSettings["qualitaetsstandard"],
                  })
                }
                className="w-full px-2 py-1 border rounded"
              >
                <option value="Basis">Basis</option>
                <option value="Standard">Standard</option>
                <option value="Gehoben">Gehoben</option>
                <option value="Premium">Premium</option>
              </select>
            </div>
            <div>
              <label className="block text-slate-500 mb-1">LV-Modus</label>
              <select
                value={project.settings.lvModus}
                onChange={(e) =>
                  patchSettings({
                    lvModus: e.target.value as LvProjectSettings["lvModus"],
                  })
                }
                className="w-full px-2 py-1 border rounded"
              >
                <option value="produktneutral">produktneutral</option>
                <option value="leitfabrikat">leitfabrikat</option>
                <option value="produktgebunden">produktgebunden</option>
              </select>
            </div>
            <div>
              <label className="block text-slate-500 mb-1">Gebäudetyp</label>
              <input
                type="text"
                value={project.settings.gebaeudetyp}
                onBlur={(e) => patchSettings({ gebaeudetyp: e.currentTarget.value })}
                onChange={(e) => {
                  setProject((prev) =>
                    prev
                      ? {
                          ...prev,
                          settings: {
                            ...prev.settings,
                            gebaeudetyp: e.target.value,
                          },
                        }
                      : prev
                  );
                }}
                className="w-full px-2 py-1 border rounded"
              />
            </div>
            <div>
              <label className="block text-slate-500 mb-1">
                Standardprofil
              </label>
              <select
                value={project.settings.standardprofilName}
                onChange={(e) =>
                  patchSettings({ standardprofilName: e.target.value })
                }
                className="w-full px-2 py-1 border rounded"
                title="Beim naechsten LV-Run via Knopfdruck wird dieses Profil als Pruefbasis verwendet."
              >
                {STANDARDPROFILE.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-slate-500 mt-1 italic">
                Aenderungen wirken erst beim naechsten LV-Run (re-transfer
                von Multi-Agent).
              </p>
            </div>
          </div>
        </div>

        {/* Hauptgruppen-Filter */}
        <div className="p-4 border-b">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">
            Hauptgruppen
          </h3>
          <div className="space-y-1">
            <button
              onClick={() => setFilterHg(null)}
              className={`w-full text-left text-xs px-2 py-1 rounded ${
                filterHg === null ? "bg-cyan-600 text-white" : "hover:bg-slate-200"
              }`}
            >
              Alle ({project.positions.length})
            </button>
            {project.hauptgruppen.map((h) => (
              <button
                key={h.code}
                onClick={() => setFilterHg(h.code)}
                className={`w-full text-left text-xs px-2 py-1 rounded ${
                  filterHg === h.code
                    ? "bg-cyan-600 text-white"
                    : "hover:bg-slate-200"
                }`}
              >
                {h.code} {h.name}{" "}
                <span className="text-slate-500">({h.positionCount})</span>
              </button>
            ))}
          </div>
        </div>

        {/* Status-Filter (Tranche-2 Ampel) */}
        <div className="p-4 border-b">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">
            Status-Filter
          </h3>
          <div className="space-y-1 text-xs">
            <button
              onClick={() => setFilterStatus("alle")}
              className={`w-full text-left px-2 py-1 rounded ${
                filterStatus === "alle"
                  ? "bg-cyan-600 text-white"
                  : "hover:bg-slate-200"
              }`}
            >
              alle ({project.positions.length})
            </button>
            {(["gruen", "gelb_gruen", "gelb", "rot"] as const).map((s) => (
              <button
                key={s}
                onClick={() => setFilterStatus(s)}
                className={`w-full text-left px-2 py-1 rounded flex items-center gap-2 ${
                  filterStatus === s
                    ? "bg-cyan-600 text-white"
                    : "hover:bg-slate-200"
                }`}
              >
                <span
                  className={`inline-block w-2 h-2 rounded-full ${STATUS_COLORS[s].bar}`}
                />
                <span>
                  {s} ({statusCounts[s]})
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Export */}
        <div className="p-4 border-b">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">Export</h3>
          <div className="flex gap-2">
            <a
              href={api.lvRunExportUrl(lvRunId, "xlsx")}
              className="flex-1 text-center text-xs px-3 py-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-700"
              download
            >
              📊 Excel
            </a>
            <a
              href={api.lvRunExportUrl(lvRunId, "json")}
              className="flex-1 text-center text-xs px-3 py-1.5 rounded bg-slate-600 text-white hover:bg-slate-700"
              download
            >
              {} JSON
            </a>
          </div>
        </div>

        {/* Warnings */}
        {project.warnings.length > 0 && (
          <div className="p-4 border-b">
            <h3 className="text-sm font-semibold text-amber-800 mb-2">
              Warnungen ({project.warnings.length})
            </h3>
            <ul className="text-[11px] text-amber-700 space-y-1 list-disc list-inside">
              {project.warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </div>
        )}
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-auto p-6 bg-white">
        {/* Klärpunkte-Banner */}
        {klaerCount > 0 && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-300 rounded flex items-center justify-between">
            <span className="text-sm text-amber-900">
              ⚠ <strong>{klaerCount}</strong> Klärpunkt(e) — Positionen mit
              Status gelb oder rot.
            </span>
            <button
              onClick={() => setFilterStatus("gelb")}
              className="text-xs px-2 py-1 bg-amber-600 text-white rounded hover:bg-amber-700"
            >
              Filtern
            </button>
          </div>
        )}

        <table className="w-full text-sm border-collapse">
          <thead className="bg-slate-100 sticky top-0">
            <tr>
              <th className="text-left px-3 py-2 font-mono text-xs">OZ_auto</th>
              <th className="text-left px-3 py-2">Bezeichnung</th>
              <th className="text-right px-3 py-2">Menge</th>
              <th className="text-left px-3 py-2">Einheit</th>
              <th className="text-left px-3 py-2">Etage</th>
              <th className="text-left px-3 py-2">WE</th>
              <th className="text-left px-3 py-2">Aufmaß</th>
              <th className="text-center px-3 py-2">Status</th>
              <th className="text-center px-3 py-2">Vertrauen</th>
              <th className="text-center px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {tree.map((row) => {
              if (row.kind === "hg") {
                const collapsed = collapsedHgs.has(row.code);
                return (
                  <tr
                    key={`hg-${row.code}`}
                    className="bg-slate-200 font-semibold border-y-2 border-slate-400 cursor-pointer hover:bg-slate-300"
                    onClick={() => toggleHg(row.code)}
                  >
                    <td className="px-3 py-2 font-mono text-xs">
                      {collapsed ? "▸" : "▾"} HG {row.code}
                    </td>
                    <td colSpan={2} className="px-3 py-2 text-sm">
                      {row.name}{" "}
                      <span className="text-xs text-slate-600">
                        ({row.positionCount} Pos.)
                      </span>
                    </td>
                    <td
                      colSpan={7}
                      className="px-3 py-2 text-right text-xs text-slate-700 font-mono"
                    >
                      Σ {formatSumByEinheit(row.sumByEinheit)}
                    </td>
                  </tr>
                );
              }
              if (row.kind === "titel") {
                const titelKey = `${row.hgCode}.${row.titelCode}`;
                const collapsed = collapsedTitels.has(titelKey);
                return (
                  <tr
                    key={`titel-${titelKey}`}
                    className="bg-slate-100 border-b border-slate-300 cursor-pointer hover:bg-slate-200"
                    onClick={() => toggleTitel(row.hgCode, row.titelCode)}
                  >
                    <td className="pl-8 pr-3 py-1.5 font-mono text-xs">
                      {collapsed ? "▸" : "▾"} {row.hgCode}.{row.titelCode}
                    </td>
                    <td colSpan={2} className="px-3 py-1.5 text-xs">
                      {row.titelName}{" "}
                      <span className="text-slate-500">
                        ({row.positionCount} Pos.)
                      </span>
                    </td>
                    <td
                      colSpan={7}
                      className="px-3 py-1.5 text-right text-[11px] text-slate-700 font-mono"
                    >
                      Σ {formatSumByEinheit(row.sumByEinheit)}
                    </td>
                  </tr>
                );
              }
              const p = row.pos;
              const isEditing = editingOz === p.ozAuto;
              const isExpanded = expandedOz === p.ozAuto;
              const ampel = p.status ?? "rot";
              const colors = STATUS_COLORS[ampel];
              const rowBg =
                ampel === "rot"
                  ? "bg-red-50"
                  : ampel === "gelb"
                    ? "bg-amber-50"
                    : ampel === "gelb_gruen"
                      ? "bg-lime-50"
                      : "";
              const aufmassCount = p.aufmass?.length ?? 0;
              const isAufmassExpanded = expandedAufmass === p.ozAuto;
              return (
              <React.Fragment key={p.ozAuto}>
                <tr
                  className={`border-b hover:bg-slate-50 ${rowBg}`}
                >
                  <td className="pl-12 pr-3 py-2 font-mono text-xs">{p.ozAuto}</td>
                  <td className="px-3 py-2">
                    {isEditing ? (
                      <input
                        value={editingDraft.bezeichnung ?? ""}
                        onChange={(e) =>
                          setEditingDraft({
                            ...editingDraft,
                            bezeichnung: e.target.value,
                          })
                        }
                        className="w-full px-1 py-0.5 border rounded text-sm"
                      />
                    ) : (
                      p.bezeichnung
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {isEditing ? (
                      <input
                        type="number"
                        step="0.01"
                        value={editingDraft.menge ?? 0}
                        onChange={(e) =>
                          setEditingDraft({
                            ...editingDraft,
                            menge: parseFloat(e.target.value),
                          })
                        }
                        className="w-24 px-1 py-0.5 border rounded text-sm text-right"
                      />
                    ) : (
                      p.menge.toFixed(2)
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {isEditing ? (
                      <input
                        value={editingDraft.einheit ?? ""}
                        onChange={(e) =>
                          setEditingDraft({
                            ...editingDraft,
                            einheit: e.target.value,
                          })
                        }
                        className="w-16 px-1 py-0.5 border rounded text-sm"
                      />
                    ) : (
                      p.einheit
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">{p.etage || "—"}</td>
                  <td className="px-3 py-2 text-xs">{p.wohneinheit ?? "—"}</td>
                  <td className="px-3 py-2 text-xs">
                    {aufmassCount > 0 ? (
                      <button
                        onClick={() =>
                          setExpandedAufmass(
                            isAufmassExpanded ? null : p.ozAuto
                          )
                        }
                        className="text-xs px-2 py-0.5 rounded border border-slate-300 hover:bg-slate-100 font-mono"
                        title={`${aufmassCount} Aufmaß-Einträge anzeigen`}
                      >
                        {isAufmassExpanded ? "▴" : "▾"} {aufmassCount}× {p.raum || "Räume"}
                      </button>
                    ) : (
                      <span className="text-slate-400">{p.raum || "—"}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span
                      className={`text-xs px-2 py-0.5 rounded font-medium ${colors.bg} ${colors.text}`}
                      title={`Pruefbasis: ${p.pruefbasis?.name ?? "—"}`}
                    >
                      {ampel}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-center">
                    {p.scores ? (
                      <div
                        className="flex items-center gap-2"
                        title={`Gesamtvertrauen ${p.scores.gesamtvertrauenScore}%`}
                      >
                        <div className="flex-1 h-2 bg-slate-200 rounded overflow-hidden min-w-[60px]">
                          <div
                            className={colors.bar}
                            style={{
                              width: `${p.scores.gesamtvertrauenScore}%`,
                              height: "100%",
                            }}
                          />
                        </div>
                        <span className="font-mono text-xs">
                          {p.scores.gesamtvertrauenScore}
                        </span>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {isEditing ? (
                      <div className="flex gap-1">
                        <button
                          onClick={savePositionEdit}
                          className="text-xs px-1.5 py-0.5 rounded bg-emerald-600 text-white hover:bg-emerald-700"
                          title="Speichern"
                        >
                          ✓
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="text-xs px-1.5 py-0.5 rounded border border-slate-300 hover:bg-slate-100"
                          title="Abbrechen"
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-1">
                        <button
                          onClick={() =>
                            setExpandedOz(isExpanded ? null : p.ozAuto)
                          }
                          className="text-xs px-1.5 py-0.5 rounded border border-slate-300 hover:bg-slate-100"
                          title="Details + Scores"
                        >
                          {isExpanded ? "▴" : "▾"}
                        </button>
                        <button
                          onClick={() => startEdit(p)}
                          className="text-xs px-1.5 py-0.5 rounded border border-slate-300 hover:bg-slate-100"
                          title="Editieren"
                        >
                          ✎
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
                {isAufmassExpanded && p.aufmass && p.aufmass.length > 0 && (
                  <tr key={`${p.ozAuto}-aufmass`} className={rowBg}>
                    <td colSpan={10} className="px-6 py-3 border-b bg-amber-50/40">
                      <div className="text-xs">
                        <h4 className="font-semibold text-slate-700 mb-2">
                          Aufmaß für {p.bezeichnung} — Σ {p.menge.toFixed(2)}{" "}
                          {p.einheit}
                        </h4>
                        <table className="w-full text-[11px]">
                          <thead className="text-slate-500">
                            <tr>
                              <th className="text-left py-1 px-2">Etage</th>
                              <th className="text-left py-1 px-2">WE</th>
                              <th className="text-left py-1 px-2">Raum</th>
                              <th className="text-left py-1 px-2">Wand-ID</th>
                              <th className="text-right py-1 px-2">Menge</th>
                              <th className="text-right py-1 px-2">Brutto</th>
                            </tr>
                          </thead>
                          <tbody>
                            {p.aufmass.map((a, i) => (
                              <tr
                                key={`${p.ozAuto}-aufmass-${i}`}
                                className="border-t border-amber-200/60"
                              >
                                <td className="py-1 px-2">{a.etage}</td>
                                <td className="py-1 px-2">
                                  {a.wohneinheit ?? "—"}
                                </td>
                                <td className="py-1 px-2">
                                  {a.raum}
                                  {a.sourceRoomId && (
                                    <span className="ml-1 text-[10px] text-slate-400 font-mono">
                                      ({a.sourceRoomId})
                                    </span>
                                  )}
                                </td>
                                <td className="py-1 px-2 font-mono text-[10px] text-slate-500">
                                  {a.wallId
                                    ? a.wallId.slice(0, 24) +
                                      (a.wallId.length > 24 ? "…" : "")
                                    : "—"}
                                </td>
                                <td className="py-1 px-2 text-right font-mono">
                                  {a.menge.toFixed(2)}
                                </td>
                                <td className="py-1 px-2 text-right font-mono text-slate-500">
                                  {a.brutto !== undefined
                                    ? a.brutto.toFixed(2)
                                    : "—"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
                {isExpanded &&
                  p.benoetigteFachplanung &&
                  p.benoetigteFachplanung.length > 0 && (
                    <tr key={`${p.ozAuto}-fachplaner`} className={rowBg}>
                      <td
                        colSpan={10}
                        className="px-6 py-3 border-b bg-sky-50/40"
                      >
                        <div className="text-xs">
                          <h4 className="font-semibold text-slate-700 mb-2">
                            Fachplaner-Nachweise — {p.bezeichnung}
                            {p.code && (
                              <span className="ml-2 text-[11px] font-mono text-slate-500">
                                ({p.code})
                              </span>
                            )}
                          </h4>
                          <table className="w-full text-[11px]">
                            <thead className="text-slate-500">
                              <tr>
                                <th className="text-left py-1 px-2 w-32">
                                  Fachplaner
                                </th>
                                <th className="text-left py-1 px-2 w-40">
                                  Status
                                </th>
                                <th className="text-left py-1 px-2">
                                  Quelle / Vermerk
                                </th>
                                <th className="text-left py-1 px-2 w-28">
                                  Geprüft am
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {p.benoetigteFachplanung.map((fp) => {
                                const n = p.nachweise?.[fp];
                                const st = n?.status ?? "fehlt";
                                const stColor =
                                  st === "geprueft" ||
                                  st === "nicht-erforderlich"
                                    ? "text-emerald-700 bg-emerald-50"
                                    : st === "vorliegt"
                                    ? "text-lime-700 bg-lime-50"
                                    : st === "angefordert"
                                    ? "text-amber-700 bg-amber-50"
                                    : "text-red-700 bg-red-50";
                                return (
                                  <tr
                                    key={`${p.ozAuto}-fp-${fp}`}
                                    className="border-t border-sky-200/60"
                                  >
                                    <td className="py-1 px-2 capitalize font-medium">
                                      {fp}
                                    </td>
                                    <td className="py-1 px-2">
                                      <select
                                        value={st}
                                        onChange={(e) =>
                                          patchNachweisStatus(
                                            p,
                                            fp,
                                            e.target.value as
                                              | "fehlt"
                                              | "angefordert"
                                              | "vorliegt"
                                              | "geprueft"
                                              | "nicht-erforderlich"
                                          )
                                        }
                                        className={`text-[11px] px-2 py-0.5 rounded border ${stColor}`}
                                      >
                                        <option value="fehlt">fehlt</option>
                                        <option value="angefordert">
                                          angefordert
                                        </option>
                                        <option value="vorliegt">
                                          vorliegt
                                        </option>
                                        <option value="geprueft">
                                          geprüft
                                        </option>
                                        <option value="nicht-erforderlich">
                                          nicht erforderlich
                                        </option>
                                      </select>
                                    </td>
                                    <td className="py-1 px-2 text-slate-600">
                                      {n?.quelle ?? n?.vermerk ?? (
                                        <span className="text-slate-400 italic">
                                          —
                                        </span>
                                      )}
                                    </td>
                                    <td className="py-1 px-2 text-[10px] text-slate-500">
                                      {n?.pruefungAm
                                        ? new Date(
                                            n.pruefungAm
                                          ).toLocaleDateString("de-DE")
                                        : "—"}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                {isExpanded && (p.langtextTemplate || p.langtext) && (
                  <tr key={`${p.ozAuto}-langtext`} className={rowBg}>
                    <td colSpan={10} className="px-6 py-3 border-b bg-emerald-50/40">
                      <div className="text-xs">
                        <h4 className="font-semibold text-slate-700 mb-2">
                          Langtext
                          {p.ozKatalog
                            ? ` · aus Katalog ${p.ozKatalog}${p.katalogVersion ? ` (${p.katalogVersion})` : ""}`
                            : p.langtext
                              ? ""
                              : " (Catalog-Template)"}
                        </h4>
                        <p className="whitespace-pre-wrap text-slate-700 leading-relaxed">
                          {p.langtext ?? p.langtextTemplate}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
                {/* LV-Katalog Phase 2 (2026-05-07) — Vorschläge aus dem
                 *  externen Korpus (z.B. dreiplus-demo). Sidebar lädt entweder
                 *  Pre-Mapping (wenn p.code) oder Live-Lookup. */}
                {isExpanded && (p.code || p.gewerk) && (
                  <tr key={`${p.ozAuto}-katalog`} className={rowBg}>
                    <td colSpan={10} className="px-6 py-3 border-b">
                      <KatalogVorschlaegeSidebar
                        bidiCode={p.code ?? null}
                        gewerk={p.gewerk}
                        einheit={p.einheit}
                        defaultOpen={false}
                        onApply={(treffer) => void applyKatalogTreffer(p, treffer)}
                      />
                    </td>
                  </tr>
                )}
                {isExpanded && p.scores && (
                  <tr key={`${p.ozAuto}-detail`} className={rowBg}>
                    <td colSpan={10} className="px-6 py-4 border-b bg-slate-50/60">
                      <div className="grid grid-cols-2 gap-6">
                        {/* 7 Scores */}
                        <div>
                          <h4 className="text-xs font-semibold uppercase text-slate-500 mb-2">
                            Scores
                          </h4>
                          <dl className="text-xs space-y-1">
                            {(
                              [
                                ["Artikel-Match", p.scores.artikelMatchScore],
                                ["Pruefbasis", p.scores.pruefbasisScore],
                                ["Nachweis", p.scores.nachweisScore],
                                ["Datenvollst.", p.scores.datenvollstaendigkeitScore],
                                ["Langtext", p.scores.langtextScore],
                                ["LV-QS", p.scores.lvQsScore],
                                ["Gesamtvertrauen", p.scores.gesamtvertrauenScore],
                              ] as const
                            ).map(([label, val]) => (
                              <div
                                key={label}
                                className="flex items-center gap-2"
                              >
                                <dt className="w-32 text-slate-600">{label}:</dt>
                                <dd className="flex-1 flex items-center gap-2">
                                  <div className="flex-1 h-1.5 bg-slate-200 rounded overflow-hidden">
                                    <div
                                      className={
                                        val >= 80
                                          ? "bg-emerald-500"
                                          : val >= 60
                                            ? "bg-lime-500"
                                            : val >= 40
                                              ? "bg-amber-500"
                                              : "bg-red-500"
                                      }
                                      style={{ width: `${val}%`, height: "100%" }}
                                    />
                                  </div>
                                  <span className="w-8 text-right font-mono">
                                    {val}
                                  </span>
                                </dd>
                              </div>
                            ))}
                          </dl>
                        </div>
                        {/* Pruefbasis + Status + Klaerpunkte */}
                        <div>
                          <h4 className="text-xs font-semibold uppercase text-slate-500 mb-2">
                            Pruefbasis + Status
                          </h4>
                          {p.pruefbasis && (
                            <dl className="text-xs space-y-1 mb-3">
                              <div className="flex">
                                <dt className="w-24 text-slate-600">Typ:</dt>
                                <dd className="font-medium">{p.pruefbasis.typ}</dd>
                              </div>
                              <div className="flex">
                                <dt className="w-24 text-slate-600">Name:</dt>
                                <dd>{p.pruefbasis.name}</dd>
                              </div>
                              <div className="flex">
                                <dt className="w-24 text-slate-600">Status:</dt>
                                <dd>{p.pruefbasis.status}</dd>
                              </div>
                              <div className="flex">
                                <dt className="w-24 text-slate-600">Nachweis:</dt>
                                <dd>
                                  {p.pruefbasis.nachweisgefuehrt ? "ja" : "nein"}
                                </dd>
                              </div>
                            </dl>
                          )}
                          <dl className="text-xs space-y-1 mb-3">
                            <div className="flex">
                              <dt className="w-24 text-slate-600">Raumtyp:</dt>
                              <dd>{p.raumtyp ?? "—"}</dd>
                            </div>
                            <div className="flex">
                              <dt className="w-24 text-slate-600">Speichern:</dt>
                              <dd>{p.speicherstatus ?? "—"}</dd>
                            </div>
                          </dl>
                          {p.klaerpunkte && p.klaerpunkte.length > 0 && (
                            <div className="mb-3">
                              <h5 className="text-xs font-semibold text-amber-800 mb-1">
                                Klaerpunkte ({p.klaerpunkte.length})
                              </h5>
                              <ul className="text-[11px] text-amber-900 list-disc list-inside space-y-0.5">
                                {p.klaerpunkte.map((k, i) => (
                                  <li key={i}>{k}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {p.warnhinweise && p.warnhinweise.length > 0 && (
                            <div>
                              <h5 className="text-xs font-semibold text-red-800 mb-1">
                                Warnhinweise
                              </h5>
                              <ul className="text-[11px] text-red-900 list-disc list-inside space-y-0.5">
                                {p.warnhinweise.map((w, i) => (
                                  <li key={i}>{w}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td
                  colSpan={10}
                  className="px-3 py-6 text-center text-slate-500 italic"
                >
                  Keine Positionen mit aktivem Filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </main>
    </div>
  );
}
